import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, StatusMessage } from 'is24-corecss'
import { OnFieldChangeType, Styles } from '../../../../@types/types'
import { formatNumberForCalc, formatNumberForDisplay } from '../../../../i18next/decimal'
import { ScreenName } from '../../../../services/domain/wizardScreens'

const styles: Styles = {
  input: {
    width: '90%',
    maxWidth: '90%',
    paddingRight: '0.3rem',
    fontFamily: 'Make It Sans IS24 Web,Verdana,DejaVu Sans,Arial,Helvetica,sans-serif',
  },
}

export interface CalculationBoxEditorProps {
  screenName: ScreenName
  calcTotalRent: number
  manualTotalRent?: string
  totalRentSpan: JSX.Element
  onChange: OnFieldChangeType
}

const CalculationBoxEditor: React.FC<CalculationBoxEditorProps> = ({
  screenName,
  calcTotalRent,
  manualTotalRent,
  totalRentSpan,
  onChange,
}) => {
  const { t } = useTranslation()
  const [editButtonState, setEditButtonState] = useState<boolean>(false)
  const [onetimeEditButtonState, setOnetimeEditButtonState] = useState<boolean>(false)
  const [showInfo, setShowInfo] = useState<boolean>(false)

  useEffect(() => {
    const bothNotEqual = calcTotalRent !== formatNumberForCalc(manualTotalRent ?? calcTotalRent.toString())
    setShowInfo(bothNotEqual)
    manualTotalRent !== undefined && manualTotalRent !== null && bothNotEqual && setEditButtonState(true)
  }, [calcTotalRent, manualTotalRent])

  const replaceNotAllowedCharacters = (value: string) => {
    const allowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ',']
    const valueArray: string[] = []
    let hasComma = false
    const resultArray: string[] = []
    Object.assign(valueArray, value)
    valueArray.forEach((char) => {
      if (char === ',' && hasComma) return
      if (char === ',' && !hasComma) hasComma = true
      allowed.includes(char) && resultArray.push(char)
    })
    return resultArray.join('')
  }

  const doChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    const changedValue = replaceNotAllowedCharacters(value)
    onChange({ screen: screenName, property: 'manualTotalRent' }, changedValue)
  }

  return (
    <>
      <span className='palm-one-half lap-five-twelfths desk-five-twelfths float-right font-nowrap align-right'>
        {editButtonState ? (
          <>
            <input
              type='tel'
              id='total-rent-input'
              aria-label={t('wizard.screen.costs.costCalculation.totalRentInput', 'Total Rent')}
              value={manualTotalRent ?? formatNumberForDisplay(calcTotalRent)}
              className='align-right font-body-medium-bold'
              autoComplete='nope'
              style={styles.input}
              onChange={doChange}
              {...(onetimeEditButtonState && { autoFocus: true })}
            />
            <span>€</span>
          </>
        ) : (
          totalRentSpan
        )}
      </span>
      {!editButtonState && (
        <div className='one-whole margin-top-s'>
          <Button
            id='edit-manual-total-rent'
            textStyle={true}
            className='palm-touch-area float-right padding-right-none font-body-medium-regular'
            aria-label={t('wizard.screen.costs.costCalculation.editTotalRent', 'Edit total rent')}
            onClick={() => {
              setEditButtonState(true)
              setOnetimeEditButtonState(true)
            }}
          >
            {t('button.edit', 'edit')}
          </Button>
        </div>
      )}
      {showInfo && (
        <StatusMessage
          className='margin-top-xl'
          statusType='info'
          onClose={() => setShowInfo(false)}
          headline={null}
          message={t(`statusMessage.messages.totalRent`)}
        />
      )}
    </>
  )
}

export default CalculationBoxEditor
